import { z } from "zod";

import { multiSelectDefaultValuesSchema } from "./general.schema";

import { ERROR_MSG } from "@/constants/ErrorMessage.constant";
import {
  alphanumericValidator,
  basicValidator,
  dateValidator,
  imageValidator,
  reactSelectValidator,
} from "@/lib/validators";

export const PoDetailsSchema = z.object({
  poNumber: alphanumericValidator({
    allowHyphen: false,
    required: false,
    allowSpace: false,
    maxLength: 10,
  }),
  poStatus: z.string().optional(),
  poFile: imageValidator(2, false, ERROR_MSG.INVALID_FILE, [
    "application/pdf",
    "pdf",
  ]),
});

export const AddProjectSchema = z.object({
  projectName: basicValidator(true, "Project Name is required", 2),
  customerId: basicValidator(true, "Customer is required"),
  customerDefaultName: z.string().optional(),
  startDate: dateValidator(true, "Required"),
  address: z
    .object({
      longitude: z.string().min(1, { message: "longitude" }),
      latitude: z.string(),
      label: z.string(),
      areaLocation: z.string().optional(),
      radius: z.coerce.number().min(1, { message: "Radius Cannot Be Empty" }),
    })
    .superRefine((data, ctx) => {
      if (data.radius < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Radius Cannot Be Empty",
        });
      }
      if (data.label.length < 3) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["label"],
          message: "Area Location is required",
        });
      }
    }),
  ourProjectTeam: reactSelectValidator(
    "Select at least one project team",
  ).superRefine((data, ctx) => {
    if (!data || data.length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Select at least one project team",
      });
    }
  }),
  defaultOurProjectTeam: multiSelectDefaultValuesSchema,
  customerProjectTeam: reactSelectValidator(
    "Select at least one team",
  ).superRefine((data, ctx) => {
    if (!data || data.length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Select at least one  team",
      });
    }
  }),
  defaultCustomerProjectTeam: multiSelectDefaultValuesSchema,
  budgetHours: z.coerce.number().optional(),
  customerProjectReference: z.string().optional(),
  ourReferenceNumber: alphanumericValidator({
    allowHyphen: true,
    required: false,
    allowSpace: false,
  }),

  inductions: z.array(
    z
      .object({
        id: z.string().optional(),
        name: z
          .string({
            required_error: "Induction Name is required.",
          })
          .superRefine((data, ctx) => {
            if (data.length < 3) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Induction Name is required.",
              });
            }
          }),
        value: z
          .string({
            required_error: "URL is required.",
          })
          .superRefine((data, ctx) => {
            if (data.length < 3) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Induction Value is required.",
              });
            }
          }),
      })
      .optional(),
  ),
});

export const UpdateProjectSchema = AddProjectSchema.partial();
export const MarkAsWonSchema = AddProjectSchema.omit({
  ourReferenceNumber: true,
}).extend({
  ourReferenceNumber: alphanumericValidator({
    allowHyphen: true,
    required: true,
    allowSpace: false,
    minLength: 1,
  }).refine(
    (data) => {
      if (!data) {
        return false;
      }
      //Split the reference number prefix and suffix
      const [, suffix] = data.split("-");

      return !!suffix;
    },
    { message: ERROR_MSG.REQUIRED },
  ),
});
export const LeadsWonSchema = MarkAsWonSchema.and(PoDetailsSchema);
export type TMarkAsWonSchema = z.infer<typeof MarkAsWonSchema>;

export type TAddProjectSchema = z.infer<typeof AddProjectSchema>;
export type TUpdateProjectSchema = z.infer<typeof UpdateProjectSchema>;
export type TLeadsWonSchema = z.infer<typeof LeadsWonSchema>;
